import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import GridLayout from 'react-grid-layout';
import { AppShell3 } from '@/components/app-shell-3';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RotateCcw, Lock, Pencil, Check, FlaskConical, GripVertical, X, PanelRightOpen, PanelRightClose, Filter } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Import react-grid-layout styles
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

// Import tile components - new responsive versions from local tiles folder
import { ComplianceOverviewTile } from './tiles/ComplianceOverviewTile';
import { ExpiringSoonTile } from './tiles/ExpiringSoonTile';
import { ExpiredRequirementsTile } from './tiles/ExpiredRequirementsTile';
import { RequirementsByStatusTile } from './tiles/RequirementsByStatusTile';
import { MyTasksTile } from './tiles/MyTasksTile';
import { TasksDueSoonTile } from './tiles/TasksDueSoonTile';
import { OverdueTasksTile } from './tiles/OverdueTasksTile';
import { TaskProgressTile } from './tiles/TaskProgressTile';
import { ImageTile } from './tiles/ImageTile';
import { TextTile } from './tiles/TextTile';
import { TitleTile } from './tiles/TitleTile';
import { TeamDirectoryTile } from './tiles/TeamDirectoryTile';
import { TeamCalendarTile } from './tiles/TeamCalendarTile';
import { PendingLeaveRequestsTile } from './tiles/PendingLeaveRequestsTile';
import { UpcomingLeaveTile } from './tiles/UpcomingLeaveTile';
import { LeaveBalanceTile } from './tiles/LeaveBalanceTile';
import { RecentPostsTile } from './tiles/RecentPostsTile';
import { AnnouncementsTile } from './tiles/AnnouncementsTile';
import { TrainingMatrixMiniTile } from './tiles/TrainingMatrixMiniTile';
// New tiles
import { BookingsOverviewTile } from './tiles/BookingsOverviewTile';
import { ChecklistsDueTile } from './tiles/ChecklistsDueTile';
import { SiteCheckinsTodayTile } from './tiles/SiteCheckinsTodayTile';
import { TrainingQuotesTile } from './tiles/TrainingQuotesTile';
import { WeatherWidgetTile } from './tiles/WeatherWidgetTile';
import { CompanyStatsTile } from './tiles/CompanyStatsTile';
import { QuickLinksTile } from './tiles/QuickLinksTile';
import { PPEStatusTile } from './tiles/PPEStatusTile';
import { DocumentsRecentTile } from './tiles/DocumentsRecentTile';
import { WorkflowsActiveTile } from './tiles/WorkflowsActiveTile';
// Legacy tiles from dashboard-test (to be migrated)
import { TeamOverviewTile } from '../dashboard-test/tiles/TeamOverviewTile';
import { NotificationsTile } from '../dashboard-test/tiles/NotificationsTile';
import { UnreadMessagesTile } from '../dashboard-test/tiles/UnreadMessagesTile';
import { QuickCheckInTile } from '../dashboard-test/tiles/QuickCheckInTile';
import { CreateTaskTile } from '../dashboard-test/tiles/CreateTaskTile';
import { MySpacesTile } from '../dashboard-test/tiles/MySpacesTile';
import { FavoriteSpacesTile } from '../dashboard-test/tiles/FavoriteSpacesTile';
import { ActionButtonTile } from '../dashboard-test/tiles/ActionButtonTile';
import { TilePaletteSidebar, TILE_META } from '../dashboard-test/TilePaletteSidebar';

// Grid configuration
const GRID_COLS = 12;
const ROW_HEIGHT = 60; // Smaller row height for finer control

// Tile component mapping
const TILE_COMPONENTS = {
  'compliance-overview': ComplianceOverviewTile,
  'expiring-soon': ExpiringSoonTile,
  'expired-requirements': ExpiredRequirementsTile,
  'requirements-status': RequirementsByStatusTile,
  'training-matrix-mini': TrainingMatrixMiniTile,
  'my-tasks': MyTasksTile,
  'tasks-due-soon': TasksDueSoonTile,
  'overdue-tasks': OverdueTasksTile,
  'task-progress': TaskProgressTile,
  'team-overview': TeamOverviewTile,
  'team-directory': TeamDirectoryTile,
  'team-calendar': TeamCalendarTile,
  'pending-leave': PendingLeaveRequestsTile,
  'upcoming-leave': UpcomingLeaveTile,
  'leave-balance': LeaveBalanceTile,
  'notifications': NotificationsTile,
  'unread-messages': UnreadMessagesTile,
  'recent-posts': RecentPostsTile,
  'announcements': AnnouncementsTile,
  'quick-checkin': QuickCheckInTile,
  'create-task': CreateTaskTile,
  'my-spaces': MySpacesTile,
  'favorite-spaces': FavoriteSpacesTile,
  'image': ImageTile,
  'text': TextTile,
  'title': TitleTile,
  // New tiles
  'bookings-overview': BookingsOverviewTile,
  'checklists-due': ChecklistsDueTile,
  'site-checkins-today': SiteCheckinsTodayTile,
  'training-quotes': TrainingQuotesTile,
  'weather-widget': WeatherWidgetTile,
  'company-stats': CompanyStatsTile,
  'quick-links': QuickLinksTile,
  'ppe-status': PPEStatusTile,
  'documents-recent': DocumentsRecentTile,
  'workflows-active': WorkflowsActiveTile,
  // Action button tiles
  'btn-create-task': ActionButtonTile,
  'btn-checkin': ActionButtonTile,
  'btn-message': ActionButtonTile,
  'btn-booking': ActionButtonTile,
  'btn-team': ActionButtonTile,
  'btn-checklists': ActionButtonTile,
};

// Size to grid dimensions mapping (h is now in 60px units)
const SIZE_TO_GRID = {
  small: { w: 1, h: 3 },    // 180px
  medium: { w: 2, h: 3 },   // 180px
  tall: { w: 1, h: 6 },     // 360px
  large: { w: 2, h: 6 },    // 360px
  xl: { w: 3, h: 6 },       // 360px
  title: { w: 2, h: 1 },    // 60px - for title tiles
};

// Default layout - user-designed dashboard (12-column grid)
const DEFAULT_LAYOUT = [
  // Top row
  { i: 'compliance-overview', x: 0, y: 0, w: 4, h: 3 },
  { i: 'expiring-soon', x: 4, y: 0, w: 3, h: 1 },
  { i: 'expired-requirements', x: 4, y: 1, w: 3, h: 2 },
  { i: 'team-directory', x: 7, y: 0, w: 3, h: 3 },
  { i: 'notifications', x: 10, y: 0, w: 2, h: 3 },

  // Second row
  { i: 'requirements-status', x: 0, y: 3, w: 4, h: 2 },
  { i: 'quick-checkin', x: 4, y: 3, w: 3, h: 2 },
  { i: 'unread-messages', x: 7, y: 3, w: 3, h: 2 },

  // Third row - tasks
  { i: 'my-tasks', x: 0, y: 5, w: 3, h: 3 },
  { i: 'task-progress', x: 3, y: 5, w: 1, h: 1 },
  { i: 'tasks-due-soon', x: 3, y: 6, w: 1, h: 1 },
  { i: 'overdue-tasks', x: 3, y: 7, w: 1, h: 1 },
  { i: 'my-spaces', x: 4, y: 5, w: 6, h: 2 },
  { i: 'favorite-spaces', x: 4, y: 7, w: 6, h: 2 },

  // Bottom row
  { i: 'create-task', x: 0, y: 8, w: 4, h: 2 },
];

// Default tiles data
const DEFAULT_TILES = [
  { id: 'compliance-overview', type: 'compliance-overview', filter: {} },
  { id: 'expiring-soon', type: 'expiring-soon', filter: {} },
  { id: 'expired-requirements', type: 'expired-requirements', filter: {} },
  { id: 'team-directory', type: 'team-directory', filter: {} },
  { id: 'notifications', type: 'notifications', filter: {} },
  { id: 'requirements-status', type: 'requirements-status', filter: {} },
  { id: 'quick-checkin', type: 'quick-checkin', filter: {} },
  { id: 'unread-messages', type: 'unread-messages', filter: {} },
  { id: 'my-tasks', type: 'my-tasks', filter: {} },
  { id: 'task-progress', type: 'task-progress', filter: {} },
  { id: 'tasks-due-soon', type: 'tasks-due-soon', filter: {} },
  { id: 'overdue-tasks', type: 'overdue-tasks', filter: {} },
  { id: 'my-spaces', type: 'my-spaces', filter: {} },
  { id: 'favorite-spaces', type: 'favorite-spaces', filter: {} },
  { id: 'create-task', type: 'create-task', filter: {} },
];

// Access denied component
const AccessDenied = () => (
  <AppShell3 pageTitle="Access Denied" pageDescription="You don't have access to this page">
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
      <div className="bg-red-100 p-6 rounded-full mb-6">
        <Lock className="h-12 w-12 text-red-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
      <p className="text-gray-600 max-w-md">
        This dashboard test page is restricted to authorized users only.
      </p>
    </div>
  </AppShell3>
);

// Tile Card wrapper component - memoized to prevent re-renders during drag
const TileCard = memo(function TileCard({ id, title, children, isEditMode, onRemove, onResize, tileW, tileH, spaces = [], filter = {}, onFilterChange }) {
  const hasFilter = filter?.spaceId;

  return (
    <Card className={cn(
      'h-full flex flex-col overflow-hidden',
      isEditMode && 'ring-1 ring-dashed ring-gray-300'
    )}>
      <CardHeader className="flex flex-row items-center justify-between py-0 px-2 h-8 border-b bg-gray-50/50 shrink-0">
        <div className="flex items-center gap-1.5">
          <div className="drag-handle cursor-grab active:cursor-grabbing p-0.5 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600">
            <GripVertical className="h-3.5 w-3.5" />
          </div>
          <CardTitle className="text-xs font-medium truncate max-w-[120px]">{title}</CardTitle>
          {hasFilter && (
            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              Filtered
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Space Filter Dropdown */}
          {spaces.length > 0 && (
            <Select
              value={filter?.spaceId || 'all'}
              onValueChange={(value) => onFilterChange?.({ ...filter, spaceId: value === 'all' ? null : value })}
            >
              <SelectTrigger
                className={cn(
                  "h-6 w-6 p-0 border-0 bg-transparent [&>svg.lucide-chevron-down]:hidden",
                  hasFilter ? "text-blue-600" : "text-gray-400 hover:text-gray-600"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <Filter className="h-3.5 w-3.5" />
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="all">All Spaces</SelectItem>
                {spaces.map((space) => (
                  <SelectItem key={space.id} value={space.id}>
                    {space.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Remove button */}
          {isEditMode && onRemove && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 p-3 overflow-hidden min-h-0">
        {children}
      </CardContent>
      {/* Custom resize controls - bypasses react-grid-layout for performance */}
      {isEditMode && onResize && (
        <div className="absolute bottom-1 right-1 flex items-center gap-0.5 bg-white/90 rounded shadow-sm border p-0.5">
          <button
            className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded text-xs font-bold"
            onClick={(e) => { e.stopPropagation(); onResize(id, -1, 0); }}
            title="Decrease width"
          >
            ←
          </button>
          <button
            className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded text-xs font-bold"
            onClick={(e) => { e.stopPropagation(); onResize(id, 1, 0); }}
            title="Increase width"
          >
            →
          </button>
          <div className="w-px h-4 bg-gray-300 mx-0.5" />
          <button
            className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded text-xs font-bold"
            onClick={(e) => { e.stopPropagation(); onResize(id, 0, -1); }}
            title="Decrease height"
          >
            ↑
          </button>
          <button
            className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded text-xs font-bold"
            onClick={(e) => { e.stopPropagation(); onResize(id, 0, 1); }}
            title="Increase height"
          >
            ↓
          </button>
        </div>
      )}
    </Card>
  );
});

// Memoized tile renderer - renders a single tile with stable references
const MemoizedTile = memo(function MemoizedTile({
  tile,
  TileComponent,
  meta,
  isEditMode,
  spaces,
  useMockData,
  onRemove,
  onFilterChange,
  onResize,
}) {
  const isDesignTile = tile.type === 'image' || tile.type === 'text' || tile.type === 'title';
  const size = tile.w >= 2 && tile.h >= 2 ? 'large' : tile.w >= 2 ? 'medium' : tile.h >= 2 ? 'tall' : 'small';
  const title = meta?.title || tile.type;

  // Design tiles render without full card wrapper
  if (isDesignTile) {
    return (
      <div className="relative group h-full">
        {/* Content - hidden via CSS during drag */}
        <div className="tile-content h-full">
          <TileComponent
            filter={tile.filter}
            size={size}
            onConfigChange={onFilterChange}
          />
        </div>
        {/* Drag handle */}
        <div className={cn(
          "drag-handle absolute top-1 left-1 cursor-grab active:cursor-grabbing p-1 rounded bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity z-10",
          isEditMode && "opacity-100"
        )}>
          <GripVertical className="h-3.5 w-3.5" />
        </div>
        {/* Remove button */}
        {isEditMode && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-1 right-1 h-6 w-6 p-0 bg-black/50 text-white hover:bg-red-600 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity z-10"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <TileCard
      id={tile.id}
      title={title}
      isEditMode={isEditMode}
      onRemove={onRemove}
      onResize={onResize}
      tileW={tile.w}
      tileH={tile.h}
      spaces={spaces}
      filter={tile.filter}
      onFilterChange={onFilterChange}
    >
      {/* Content - hidden via CSS during drag */}
      <div className="tile-content h-full">
        <TileComponent
          filter={tile.filter}
          size={size}
          useMockData={useMockData}
          onConfigChange={onFilterChange}
        />
      </div>
    </TileCard>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific props change
  // Skip function comparisons (onRemove, onFilterChange) since they're stored in refs
  return (
    prevProps.tile.id === nextProps.tile.id &&
    prevProps.tile.type === nextProps.tile.type &&
    prevProps.tile.w === nextProps.tile.w &&
    prevProps.tile.h === nextProps.tile.h &&
    prevProps.tile.filter?.spaceId === nextProps.tile.filter?.spaceId &&
    prevProps.isEditMode === nextProps.isEditMode &&
    prevProps.useMockData === nextProps.useMockData &&
    prevProps.spaces === nextProps.spaces
  );
});

export default function DashboardTestGridPage() {
  const { user, userProfile, company } = useAuth();
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [tiles, setTiles] = useState(DEFAULT_TILES);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configLoaded, setConfigLoaded] = useState(false); // Prevent saving until config is loaded
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [containerWidth, setContainerWidth] = useState(1200);
  const containerRef = useRef(null);
  const initialRenderRef = useRef(true); // Track first render to ignore initial onLayoutChange

  const hasAccess = !!user; // Allow all authenticated users

  // Measure container width on mount and window resize only
  useEffect(() => {
    const updateWidth = () => {
      const container = containerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        setContainerWidth(Math.floor(rect.width));
      }
    };

    // Initial measurement after mount
    updateWidth();

    // Only update on window resize, not container resize (avoids feedback loop)
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Load dashboard config
  useEffect(() => {
    if (!hasAccess || !userProfile?.id) return;

    const loadConfig = async () => {
      try {
        const { data, error } = await supabase
          .from('dashboard_configs')
          .select('*')
          .eq('user_id', userProfile.id)
          .eq('config_name', 'grid-layout')
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading dashboard config:', error);
        }

        if (data?.tiles && Array.isArray(data.tiles) && data.tiles.length > 0) {
          setTiles(data.tiles);
          // Convert tiles to layout with type-specific defaults
          const newLayout = data.tiles.map(t => {
            const isTitleTile = t.type === 'title';
            // Default height: 1 row (60px) for title, 3 rows (180px) for others
            const defaultH = isTitleTile ? 1 : 3;
            return {
              i: t.id,
              x: t.x ?? 0,
              y: t.y ?? 0,
              w: t.w ?? (isTitleTile ? 2 : 1),
              h: t.h ?? defaultH,
            };
          });
          setLayout(newLayout);
        }
        // Mark config as loaded - safe to save now
        setConfigLoaded(true);
      } catch (err) {
        console.error('Failed to load config:', err);
        setConfigLoaded(true); // Still allow saving after error
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [hasAccess, userProfile?.id]);

  // Load spaces
  useEffect(() => {
    if (!hasAccess || !company?.id) return;

    const loadSpaces = async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('company_id', company.id)
        .eq('is_archived', false)
        .order('name');

      if (!error && data) {
        setSpaces(data);
      }
    };

    loadSpaces();
  }, [hasAccess, company?.id]);

  // Save config
  const saveConfig = useCallback(async (newTiles, newLayout) => {
    if (!userProfile?.id) return;
    if (!configLoaded) {
      console.warn('Skipping save - config not yet loaded from database');
      return;
    }

    // Merge layout positions into tiles
    const tilesWithLayout = newTiles.map(t => {
      const layoutItem = newLayout.find(l => l.i === t.id);
      return {
        ...t,
        x: layoutItem?.x ?? 0,
        y: layoutItem?.y ?? 0,
        w: layoutItem?.w ?? 1,
        h: layoutItem?.h ?? 1,
      };
    });

    setSaving(true);
    try {
      await supabase
        .from('dashboard_configs')
        .upsert({
          user_id: userProfile.id,
          config_name: 'grid-layout',
          tiles: tilesWithLayout,
          updated_date: new Date().toISOString(),
        }, {
          onConflict: 'user_id,config_name',
        });
    } catch (err) {
      console.error('Failed to save config:', err);
      toast.error('Failed to save dashboard layout');
    } finally {
      setSaving(false);
    }
  }, [userProfile?.id, configLoaded]);

  // Track if we're currently dragging/resizing - use ref to avoid re-renders
  const isDraggingRef = useRef(false);

  // Handle layout change - DON'T update state during drag (causes lag)
  const handleLayoutChange = useCallback((newLayout) => {
    // Skip during drag/resize - library handles visuals internally
    if (isDraggingRef.current) return;

    // Skip the very first onLayoutChange call (fired on mount by react-grid-layout)
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }
    setLayout(newLayout);
  }, []);

  // Mark start of drag - use DOM manipulation to avoid React re-render
  const handleDragResizeStart = useCallback(() => {
    isDraggingRef.current = true;
    // Add class to container for CSS-based hiding of content
    containerRef.current?.classList.add('is-dragging');
  }, []);

  // Save to database only when drag/resize stops
  const handleDragResizeStop = useCallback((newLayout) => {
    isDraggingRef.current = false;
    // Remove class from container
    containerRef.current?.classList.remove('is-dragging');
    setLayout(newLayout);
    saveConfig(tiles, newLayout);
  }, [tiles, saveConfig]);

  // Custom resize handler for smooth performance - bypasses react-grid-layout
  const handleCustomResize = useCallback((tileId, deltaW, deltaH) => {
    setLayout(prev => {
      const newLayout = prev.map(item => {
        if (item.i === tileId) {
          return {
            ...item,
            w: Math.max(1, Math.min(12, item.w + deltaW)), // Clamp between 1 and 12 cols
            h: Math.max(1, item.h + deltaH),
          };
        }
        return item;
      });
      // Save immediately after resize
      saveConfig(tiles, newLayout);
      return newLayout;
    });
  }, [tiles, saveConfig]);

  // Handle reset
  const handleReset = useCallback(() => {
    setLayout(DEFAULT_LAYOUT);
    setTiles(DEFAULT_TILES);
    saveConfig(DEFAULT_TILES, DEFAULT_LAYOUT);
    toast.success('Dashboard reset to default layout');
  }, [saveConfig]);

  // Handle add tile
  const handleAddTile = useCallback((tileType) => {
    const meta = TILE_META[tileType];
    // Use 'title' size for title tiles, otherwise use meta default
    const sizeKey = tileType === 'title' ? 'title' : (meta?.defaultSize || 'small');
    const gridSize = SIZE_TO_GRID[sizeKey];

    // Map button tile types to their action types
    const buttonActionMap = {
      'btn-create-task': 'create-task',
      'btn-checkin': 'new-checkin',
      'btn-message': 'new-message',
      'btn-booking': 'new-booking',
      'btn-team': 'view-team',
      'btn-checklists': 'view-checklists',
    };

    // Set filter with actionType for button tiles
    const filter = buttonActionMap[tileType]
      ? { actionType: buttonActionMap[tileType] }
      : {};

    const newTile = {
      id: `${tileType}-${Date.now()}`,
      type: tileType,
      filter,
    };

    // Find position for new tile (bottom of grid)
    const maxY = layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);

    const newLayoutItem = {
      i: newTile.id,
      x: 0,
      y: maxY,
      w: gridSize.w,
      h: gridSize.h,
    };

    const newTiles = [...tiles, newTile];
    const newLayout = [...layout, newLayoutItem];

    setTiles(newTiles);
    setLayout(newLayout);
    saveConfig(newTiles, newLayout);
    toast.success(`Added ${meta?.title || tileType}`);
  }, [tiles, layout, saveConfig]);

  // Handle remove tile
  const handleRemoveTile = useCallback((tileId) => {
    const newTiles = tiles.filter(t => t.id !== tileId);
    const newLayout = layout.filter(l => l.i !== tileId);

    setTiles(newTiles);
    setLayout(newLayout);
    saveConfig(newTiles, newLayout);
    toast.success('Tile removed');
  }, [tiles, layout, saveConfig]);

  // Handle filter change
  const handleFilterChange = useCallback((tileId, newFilter) => {
    const newTiles = tiles.map(t =>
      t.id === tileId ? { ...t, filter: newFilter } : t
    );
    setTiles(newTiles);
    saveConfig(newTiles, layout);
  }, [tiles, layout, saveConfig]);

  // Create stable callback maps for remove and filter change
  // This prevents creating new function references on every render
  const removeCallbacksRef = useRef({});
  const filterCallbacksRef = useRef({});

  // Update callbacks when handlers change, but keep references stable per tile
  useMemo(() => {
    tiles.forEach(tile => {
      removeCallbacksRef.current[tile.id] = () => handleRemoveTile(tile.id);
      filterCallbacksRef.current[tile.id] = (newFilter) => handleFilterChange(tile.id, newFilter);
    });
  }, [tiles, handleRemoveTile, handleFilterChange]);

  // Memoize the rendered tiles to prevent recreation during drag
  const renderedTiles = useMemo(() => {
    return tiles.filter(t => !t.type.startsWith('btn-')).map((tile) => {
      const TileComponent = TILE_COMPONENTS[tile.type];
      const meta = TILE_META[tile.type];
      const layoutItem = layout.find(l => l.i === tile.id);

      if (!TileComponent) return null;

      // Merge tile with layout dimensions for size calculation
      const tileWithLayout = {
        ...tile,
        w: layoutItem?.w ?? 1,
        h: layoutItem?.h ?? 3,
      };

      return (
        <div key={tile.id} className="bg-white rounded-lg shadow-sm">
          <MemoizedTile
            tile={tileWithLayout}
            TileComponent={TileComponent}
            meta={meta}
            isEditMode={isEditMode}
            spaces={spaces}
            useMockData={useMockData}
            onRemove={removeCallbacksRef.current[tile.id]}
            onFilterChange={filterCallbacksRef.current[tile.id]}
            onResize={isEditMode ? handleCustomResize : undefined}
          />
        </div>
      );
    });
  }, [tiles, layout, isEditMode, spaces, useMockData, handleCustomResize]);

  if (!hasAccess) {
    return <AccessDenied />;
  }

  return (
    <AppShell3
      pageTitle="Dashboard Test (React Grid)"
      pageDescription="Using react-grid-layout for comparison"
    >
      <div className={cn("container mx-auto px-4 py-6 transition-all", sidebarOpen && "pr-[340px]")}>
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-500">
              {saving ? 'Saving...' : 'Using react-grid-layout. Drag tiles to reposition, drag corners to resize.'}
            </p>
            {/* Mock Data Toggle */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
              <FlaskConical className="h-4 w-4 text-amber-600" />
              <Label htmlFor="mock-data-grid" className="text-sm text-amber-700 cursor-pointer">
                Simulate Data
              </Label>
              <Switch
                id="mock-data-grid"
                checked={useMockData}
                onCheckedChange={setUseMockData}
                className="data-[state=checked]:bg-amber-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button
              variant={isEditMode ? "default" : "outline"}
              size="sm"
              onClick={() => {
                if (isEditMode) {
                  setSidebarOpen(false);
                }
                setIsEditMode(!isEditMode);
              }}
            >
              {isEditMode ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Done
                </>
              ) : (
                <>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </>
              )}
            </Button>
          </div>

          {/* Sidebar Toggle Tab */}
          {isEditMode && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={cn(
                "fixed right-0 top-1/2 -translate-y-1/2 z-40",
                "flex items-center gap-2 px-2 py-4",
                "bg-primary text-white rounded-l-lg shadow-lg",
                "hover:bg-primary/90 transition-all",
                sidebarOpen && "right-80"
              )}
            >
              {sidebarOpen ? (
                <PanelRightClose className="h-5 w-5" />
              ) : (
                <>
                  <PanelRightOpen className="h-5 w-5" />
                  <span className="text-sm font-medium [writing-mode:vertical-lr] rotate-180">Add Tiles</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Button Bar - Separate section for action buttons */}
        {(() => {
          const buttonTiles = tiles.filter(t => t.type.startsWith('btn-'));
          if (buttonTiles.length === 0 && !isEditMode) return null;

          return (
            <div className={cn(
              "mb-4 p-3 rounded-lg",
              isEditMode && "border-2 border-dashed border-gray-300 bg-gray-50/30"
            )}>
              {isEditMode && buttonTiles.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-2">
                  Add action buttons from the sidebar
                </p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {buttonTiles.map((tile) => {
                    const TileComponent = TILE_COMPONENTS[tile.type];
                    if (!TileComponent) return null;

                    return (
                      <div key={tile.id} className="relative group">
                        <TileComponent
                          filter={tile.filter}
                          onConfigChange={(newFilter) => handleFilterChange(tile.id, newFilter)}
                          compact={true}
                        />
                        {/* Remove button in edit mode */}
                        {isEditMode && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute -top-2 -right-2 h-5 w-5 p-0 bg-red-500 text-white hover:bg-red-600 rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            onClick={() => handleRemoveTile(tile.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* Grid Container */}
        <div
          ref={containerRef}
          className={cn(
            "rounded-lg",
            isEditMode && "border-2 border-dashed border-gray-300 bg-gray-50/30"
          )}
        >
          {loading ? (
            <div className="grid grid-cols-4 gap-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-square bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <GridLayout
              className="layout"
              layout={layout.filter(l => !l.i.startsWith('btn-'))}
              cols={GRID_COLS}
              rowHeight={ROW_HEIGHT}
              width={containerWidth}
              onLayoutChange={handleLayoutChange}
              onDragStart={handleDragResizeStart}
              onDragStop={(newLayout) => handleDragResizeStop(newLayout)}
              isDraggable={true}
              isResizable={false}
              draggableHandle=".drag-handle"
              compactType={null}
              preventCollision={false}
              margin={[12, 12]}
              containerPadding={[0, 0]}
              transformScale={1}
              useCSSTransforms={true}
            >
              {renderedTiles}
            </GridLayout>
          )}

          {/* Empty state */}
          {!loading && tiles.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <p className="text-lg mb-2">No tiles added yet</p>
              <p className="text-sm">Click Edit to add tiles from the sidebar</p>
            </div>
          )}
        </div>
      </div>

      {/* Tile Palette Sidebar */}
      <TilePaletteSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activeTileTypes={tiles.map(t => t.type)}
        onAddTile={handleAddTile}
      />
    </AppShell3>
  );
}
